import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type {
  RequestConfig,
  RequestOptions,
  RunTimeLayoutConfig,
} from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import defaultSettings from '../config/defaultSettings';
import '@ant-design/v5-patch-for-react-19';
import { message } from 'antd';
import { adminInfoApi, currentTenantInfoApi } from './services/apis';

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: IAdmin;
  tenantInfo?: ITenant;
  loading?: boolean;
  fetchUserInfo?: () => Promise<IAdmin | undefined>;
  fetchTenantInfo?: () => Promise<ITenant | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const { data } = await adminInfoApi();
      return data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  const fetchTenantInfo = async () => {
    try {
      const { data } = await currentTenantInfoApi();
      return data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;

  if (location.pathname !== loginPath) {
    const token = localStorage.getItem('token');

    if (!token) {
      return {
        fetchUserInfo,
        fetchTenantInfo,
        settings: defaultSettings as Partial<LayoutSettings>,
      };
    }

    const { data } = await adminInfoApi();

    const { data: tenantInfo } = data.tenantId
      ? await currentTenantInfoApi()
      : { data: null };

    return {
      fetchUserInfo,
      tenantInfo: tenantInfo || undefined,
      settings: defaultSettings as Partial<LayoutSettings>,
      currentUser: data,
    };
  } else {
    return {
      fetchUserInfo,
      fetchTenantInfo,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    avatarProps: {
      title: initialState?.currentUser?.name,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    logo: false,
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      const token = localStorage.getItem('token');
      if (!token && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */

export const request: RequestConfig = {
  baseURL:
    process.env.NODE_ENV === 'development'
      ? 'https://w.ankw.cn/api'
      // : 'https://wx.ameyy.com/api https://hyxy.gzhgzyyy.com/api  http://localhost:7877/api',
  :'/api',
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: token,
        };
      }
      return config;
    },
  ],
  responseInterceptors: [
    (response) => {
      if (response.status !== 200) {
        return Promise.reject(response);
      }
      const { data } = response as unknown as IBaseResponse<any>;

      if (data.code === 401) {
        localStorage.removeItem('token');
        window.location.href = loginPath;
        return Promise.reject(new Error(data.message));
      }

      if (data.code !== 0) {
        return Promise.reject(new Error(data.message));
      }

      return Promise.resolve(response);
    },
  ],
  errorConfig: {
    errorHandler: (error) => {
      console.log('errorHandler', error);

      message.error(error.message);
    },
  },
};
